import { ValueFormatterParams } from 'ag-grid-community';
import {
  differenceInCalendarDays,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import { KpiPeriod } from '../../../data-models/company-financials.data-model';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { IKPITemplate } from '../../../data-models/kpi-template.data-model';
import {
  CompanyPeriodRequest,
  CompanyPeriodRequestStatus,
  DataPointPeriodicity,
} from '../../../schemas/CompanyPeriodRequest.schema';
import { V2FirmRequestItem, V2UniversalRequestItem } from '../../../schemas/CompanyPeriodRequestV2.schema';
import { extractISODate, plainDateToZonedDateTime } from '../../../util/date-utilts';
import { isProduction } from '../../../util/environment';
import { fiscalDateFormatter2 } from '../../../util/formatters/DateFormatters';
import { CompanyPeriodRequestManageViewModel, V2RequestType } from './company-period-request.view-model';

/** Sentinel value for the period dropdown meaning "every available period for the frequency". */
export const ALL_PERIODS = 'all';

/** Universal requests aren't backed by a firm template; they show this template name. */
export const UNIVERSAL_TEMPLATE_NAME = 'Foresight Universal';

/**
 * Periods are bucketed on the firm-wide FYE-12 fiscal calendar (quarters ending
 * Mar/Jun/Sep/Dec), mirroring the BE multi-period logic: fyStart = (fye % 12) + 1,
 * quarter = 3-month block into the fiscal year. There is no firm-level FYE
 * setting, so FYE 12 is the agreed firm calendar; per-company FYEs only affect
 * the grid's Period column display, not the dropdown buckets.
 */
export const FIRM_FYE = 12;

/** An option in the period dropdown. `value` is the FYE-12 period-end ISO date. */
export interface AvailablePeriod {
  value: string;
  label: string;
}

/**
 * Collapses a raw request `period` (any date within the period) to the FYE-12
 * period-end ISO date for its periodicity. All requests falling in the same
 * firm-calendar period therefore share one bucket, regardless of company FYE
 * (e.g. a Jan-FYE company's 2026-01-15 quarter magnetizes into Q1 2026).
 */
export function canonicalPeriodEndISO(period: string, periodicity: string): string {
  const date = parseISO(period);
  const end =
    periodicity === DataPointPeriodicity.month
      ? endOfMonth(date)
      : periodicity === DataPointPeriodicity.quarter
        ? endOfQuarter(date)
        : endOfYear(date);
  return format(end, 'yyyy-MM-dd');
}

function firmPeriodLabel(periodEndISO: string, periodicity: string): string {
  return fiscalDateFormatter2({
    period: RequestPeriodToKpiPeriod[periodicity as DataPointPeriodicity],
    periodISODate: periodEndISO,
    fye: FIRM_FYE,
  });
}

/**
 * The exact date span of a firm-calendar period bucket: [period start, period end].
 * Mirrors `canonicalPeriodEndISO` so the export covers precisely the requests the
 * grid shows for that bucket (e.g. Q1 2026 → Jan 1 – Mar 31).
 */
function periodExportRange(
  periodEndISO: string,
  periodicity: DataPointPeriodicity
): { dateFrom: string; dateTo: string } {
  const end = parseISO(periodEndISO);
  const start =
    periodicity === DataPointPeriodicity.month
      ? startOfMonth(end)
      : periodicity === DataPointPeriodicity.quarter
        ? startOfQuarter(end)
        : startOfYear(end);
  return {
    dateFrom: format(start, 'yyyy-MM-dd'),
    dateTo: periodEndISO,
  };
}

/**
 * Resolves the export date range from the current frequency + period selection,
 * matching what the grid shows. A specific period exports exactly its bucket's
 * span; "All periods" spans the earliest bucket's start to the latest bucket's
 * end. Empty when no periods are available.
 */
export function getExportDateRange(
  selectedPeriod: string,
  availablePeriods: AvailablePeriod[],
  selectedFrequency: DataPointPeriodicity
): { dateFrom?: string; dateTo?: string } {
  if (selectedPeriod !== ALL_PERIODS) {
    return periodExportRange(selectedPeriod, selectedFrequency);
  }
  if (availablePeriods.length === 0) {
    return {};
  }
  const sorted = availablePeriods.map((p) => p.value).sort();
  return {
    dateFrom: periodExportRange(sorted[0], selectedFrequency).dateFrom,
    dateTo: sorted[sorted.length - 1],
  };
}

const StatusesToShow = new Set<CompanyPeriodRequestStatus>([
  CompanyPeriodRequestStatus.sent,
  CompanyPeriodRequestStatus.submitted,
  CompanyPeriodRequestStatus.processing,
  CompanyPeriodRequestStatus.accepted,
  CompanyPeriodRequestStatus.rejected,
]);

/**
 * Returns array of all CompanyPeriodRequestStatus values
 */
export function getAllCompanyPeriodRequestStatuses(): CompanyPeriodRequestStatus[] {
  return Array.from(StatusesToShow);
}

/**
 * Status tabs for the V2 page. `submitted` is merged into `processing`, so it is
 * not listed separately — rows in either state are shown (and counted) as processing.
 */
const V2DisplayStatuses: CompanyPeriodRequestStatus[] = [
  CompanyPeriodRequestStatus.sent,
  CompanyPeriodRequestStatus.processing,
  CompanyPeriodRequestStatus.accepted,
  CompanyPeriodRequestStatus.rejected,
];

export function getV2CompanyPeriodRequestStatuses(): CompanyPeriodRequestStatus[] {
  return [...V2DisplayStatuses];
}

/** Collapses the `submitted` status into `processing` for V2 display + filtering. */
function mergeSubmittedIntoProcessing(status: CompanyPeriodRequestStatus): CompanyPeriodRequestStatus {
  return status === CompanyPeriodRequestStatus.submitted ? CompanyPeriodRequestStatus.processing : status;
}

export function getRequestsToShow(requests: CompanyPeriodRequest[]) {
  return requests.filter((request) => StatusesToShow.has(request.status) && request.maggieCompanyId != null);
}
interface MapCompanyPeriodRequestsToViewModelParams {
  requests: CompanyPeriodRequest[];
  companies: ICompanyDataModel[];
  from: Date;
  to: Date;
  selectedCompanyId?: number;
}
/**
 * Maps CompanyPeriodRequest data combined with KpiCollectionCompanySettings and company data
 * into a view model suitable for display in the grid.
 *
 * @param params - Object containing:
 *   - requests: Array of CompanyPeriodRequest objects
 *   - companies: Array of ICompanyDataModel objects
 *   - from: Start date for filtering (inclusive)
 *   - to: End date for filtering (inclusive)
 *   - selectedCompanyId: Optional company ID to filter by
 * @returns Object containing mapped requests, included companies map, and request statistics
 */

export function mapCompanyPeriodRequestsToViewModel(params: MapCompanyPeriodRequestsToViewModelParams): {
  requests: CompanyPeriodRequestManageViewModel[];
  includedCompanies: Map<number, string>;
  requestStats: Map<CompanyPeriodRequestStatus | string, number>;
} {
  const { requests, companies, from, to, selectedCompanyId } = params;
  const companiesById = companies.reduce(
    (acc, company) => {
      acc[company.id] = company;
      return acc;
    },
    {} as Record<number, ICompanyDataModel>
  );

  const includedCompanies = new Map<number, string>();
  const requestStats = new Map<CompanyPeriodRequestStatus | string, number>();

  const requestsForPeriod = filterByDateRange(requests, from, to, (r) => r.period);

  const filteredByCompany = requestsForPeriod.filter(
    (request) => selectedCompanyId == null || request.maggieCompanyId === selectedCompanyId
  );

  filteredByCompany.forEach((request) => {
    const company = companiesById[request.maggieCompanyId ?? -1];
    if (company) {
      includedCompanies.set(company.id, company.name);
      const statusCount = requestStats.get(request.status) ?? 0;
      requestStats.set(request.status, statusCount + 1);
    }
  });

  const mappedRequests: CompanyPeriodRequestManageViewModel[] = filteredByCompany.reduce((acc, request) => {
    const company = companiesById[request.maggieCompanyId ?? -1];
    if (company) {
      acc.push({
        id: request.id,
        companyId: company.id,
        companyName: company.name,
        fye: company.fye,
        status: request.status,
        period: request.period,
        periodicity: request.periodicity,
      });
    }
    return acc;
  }, [] as CompanyPeriodRequestManageViewModel[]);

  requestStats.set('All', mappedRequests.length);

  return {
    requests: mappedRequests,
    includedCompanies,
    requestStats,
  };
}

export const RequestPeriodToKpiPeriod: Record<DataPointPeriodicity, KpiPeriod> = {
  [DataPointPeriodicity.month]: KpiPeriod.month,
  [DataPointPeriodicity.quarter]: KpiPeriod.quarter,
  [DataPointPeriodicity.year]: KpiPeriod.year,
};

/** Fiscal label for a request period (e.g. "Q1 2026" / "June 2024"), FYE-aware. */
export function formatRequestPeriodLabel(
  data: Pick<CompanyPeriodRequestManageViewModel, 'period' | 'periodicity' | 'fye'>
): string {
  if (!data.period || !data.periodicity) {
    return '';
  }
  return fiscalDateFormatter2({
    period: RequestPeriodToKpiPeriod[data.periodicity],
    periodISODate: data.period,
    fye: data.fye ?? 12,
  });
}

export function periodValueFormatter(params: ValueFormatterParams<CompanyPeriodRequestManageViewModel>) {
  if (!params.data) {
    return '';
  }
  return formatRequestPeriodLabel(params.data);
}

/** Period-column tooltip, e.g. "FYE: Dec" (December when the company has no FYE set). */
export function formatFyeTooltip(fye: number | null | undefined): string {
  return `FYE: ${format(new Date(2000, (fye ?? 12) - 1, 1), 'MMM')}`;
}

export interface DueDateDisplay {
  /** Custom label; when absent, show the plain date in the severity colour. */
  label?: string;
  severity: 'due-soon' | 'overdue';
}

/**
 * Special Due Date display while a request is still awaiting a response
 * (status `sent`):
 *  - due within 2 days → "Due in X days" (warning)
 *  - overdue within 2 days → "Overdue by X days" (error)
 *  - overdue by more than 2 days → plain date kept in the error colour (no label)
 *  - otherwise (>2 days out, completed states, no due date) → null = plain date.
 */
export function getDueDateDisplay(
  row: Pick<CompanyPeriodRequestManageViewModel, 'dueDate' | 'status'>,
  today: Date = new Date()
): DueDateDisplay | null {
  if (!row.dueDate || row.status !== CompanyPeriodRequestStatus.sent) {
    return null;
  }
  const days = differenceInCalendarDays(parseISO(row.dueDate), today);
  if (days < 0) {
    const overdueBy = Math.abs(days);
    if (overdueBy > 2) {
      return { severity: 'overdue' };
    }
    return { label: `Overdue by ${overdueBy} day${overdueBy === 1 ? '' : 's'}`, severity: 'overdue' };
  }
  if (days <= 2) {
    return {
      label: days === 0 ? 'Due today' : `Due in ${days} day${days === 1 ? '' : 's'}`,
      severity: 'due-soon',
    };
  }
  return null;
}

/**
 * Builds the "remind respondents" mailto for a request: recipients in the To,
 * a default body asking them to complete the request for the given period via
 * the data collection portal (prod vs non-prod aware).
 */
export function buildRequestReminderMailtoHref(
  row: Pick<CompanyPeriodRequestManageViewModel, 'period' | 'periodicity' | 'fye' | 'recipients'>
): string {
  const periodLabel = formatRequestPeriodLabel(row);
  // Full https:// URL so mail clients render it as a clickable link (mailto bodies are plain text).
  const portalUrl = isProduction() ? 'https://data.foresight.ai/' : 'https://data.dev.foresight.ai/';
  const to = (row.recipients ?? []).join(',');
  const subject = `Data Collection Request${periodLabel ? ` - ${periodLabel}` : ''}`;
  const body = `Hi,\n\nThis is a reminder to please submit your response to the information request for the period of ${periodLabel}. You can log in at ${portalUrl} to complete this. Please let us know if you have any questions.\n\nThank you!`;
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export const DefaultDateRangeMonths = 3;

// Rows whose accessor returns null/undefined are kept — they fall outside the range concept
function filterByDateRange<T>(
  rows: T[],
  from: Date,
  to: Date,
  getDate: (row: T) => string | null | undefined
): T[] {
  return rows.filter((row) => {
    const dateStr = getDate(row);
    if (dateStr == null) {
      return true;
    }
    try {
      const date = plainDateToZonedDateTime(dateStr);
      return date >= from && date <= to;
    } catch {
      return false;
    }
  });
}

interface MapV2CompanyPeriodRequestsToViewModelParams {
  universalRequests: V2UniversalRequestItem[];
  firmRequests: V2FirmRequestItem[];
  companies: ICompanyDataModel[];
  /** Frequency filter — only requests with this periodicity are shown. */
  selectedFrequency: DataPointPeriodicity;
  /** Canonical calendar period-end ISO date, or `ALL_PERIODS`. */
  selectedPeriod: string;
  /** KPI templates keyed by uuid; used to resolve a firm request's template name. */
  templatesByUuid?: Map<string, IKPITemplate>;
}

/**
 * Resolves a firm request's display template name from the KPI templates data
 * (by uuid), falling back to any embedded template snapshot name.
 */
function resolveFirmTemplateName(
  req: V2FirmRequestItem,
  templatesByUuid: Map<string, IKPITemplate>
): string | null {
  if (req.templateUuid) {
    const name = templatesByUuid.get(req.templateUuid)?.name;
    if (name) {
      return name;
    }
  }
  return req.template?.name ?? null;
}

/**
 * Filter order: status (at construction) → frequency → period. Company filtering
 * is done directly in the grid. The period dropdown options reflect the selected
 * frequency; the grid rows + status counts reflect frequency + period.
 */
export function mapV2CompanyPeriodRequestsToViewModel(params: MapV2CompanyPeriodRequestsToViewModelParams): {
  requests: CompanyPeriodRequestManageViewModel[];
  requestStats: Map<CompanyPeriodRequestStatus | string, number>;
  availablePeriods: AvailablePeriod[];
} {
  const {
    universalRequests,
    firmRequests,
    companies,
    selectedFrequency,
    selectedPeriod,
    templatesByUuid = new Map<string, IKPITemplate>(),
  } = params;

  const companiesById = new Map(companies.map((company) => [company.id, company]));

  const allRows = [
    ...universalRequests
      .filter((req) => StatusesToShow.has(req.status as CompanyPeriodRequestStatus))
      .map((req) => toV2Row(req, V2RequestType.universal, UNIVERSAL_TEMPLATE_NAME)),
    ...firmRequests
      .filter((req) => StatusesToShow.has(req.status as CompanyPeriodRequestStatus))
      .map((req) => toV2Row(req, V2RequestType.firm, resolveFirmTemplateName(req, templatesByUuid))),
  ].filter((row) => companiesById.has(row.companyId));

  const byFrequency = allRows.filter((row) => row.periodicity === selectedFrequency);

  // Period dropdown options: distinct FYE-12 firm-calendar periods, newest first.
  // Requests from companies with other FYEs magnetize into these buckets.
  const periodLabels = new Map<string, string>();
  byFrequency.forEach((row) => {
    const periodEnd = canonicalPeriodEndISO(row.period, row.periodicity);
    if (!periodLabels.has(periodEnd)) {
      periodLabels.set(periodEnd, firmPeriodLabel(periodEnd, row.periodicity));
    }
  });
  const availablePeriods: AvailablePeriod[] = Array.from(periodLabels, ([value, label]) => ({
    value,
    label,
  })).sort((a, b) => b.value.localeCompare(a.value));

  const byPeriod = byFrequency.filter(
    (row) =>
      selectedPeriod === ALL_PERIODS || canonicalPeriodEndISO(row.period, row.periodicity) === selectedPeriod
  );

  const requestStats = new Map<CompanyPeriodRequestStatus | string, number>();
  const mappedRequests: CompanyPeriodRequestManageViewModel[] = [];

  byPeriod.forEach((row) => {
    const company = companiesById.get(row.companyId);
    if (!company) {
      return;
    }

    const status = mergeSubmittedIntoProcessing(row.status);
    requestStats.set(status, (requestStats.get(status) ?? 0) + 1);

    mappedRequests.push({
      id: row.id,
      companyId: company.id,
      companyName: company.name,
      fye: company.fye,
      status,
      period: row.period,
      periodicity: row.periodicity,
      sentAt: row.sentAt,
      dueDate: row.dueDate,
      requestType: row.requestType,
      templateName: row.templateName,
      recipients: company.respondents?.filter(Boolean) ?? [],
      emailStatus: row.emailStatus,
      emailFailedCount: row.emailFailedCount,
      emailTotalCount: row.emailTotalCount,
    });
  });

  requestStats.set('All', mappedRequests.length);

  return { requests: mappedRequests, requestStats, availablePeriods };
}

function toV2Row(
  req: V2UniversalRequestItem | V2FirmRequestItem,
  requestType: V2RequestType,
  templateName: string | null
) {
  return {
    id: req.id,
    companyId: req.companyId,
    period: req.period,
    periodicity: req.periodicity,
    status: req.status,
    sentAt: req.sentAt == null ? null : extractISODate(req.sentAt),
    dueDate: req.dueDate == null ? null : extractISODate(req.dueDate),
    requestType,
    templateName,
    // Default for a pre-rollup backend that omits the fields.
    emailStatus: req.emailStatus ?? null,
    emailFailedCount: req.emailFailedCount ?? 0,
    emailTotalCount: req.emailTotalCount ?? 0,
  };
}
